// src/contexts/OpenViduContext.js
import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { OpenVidu, Publisher, Session, Subscriber } from 'openvidu-browser';
import axios from 'axios';
import { SIGNALING_CONFIG } from '../../signaling-config';

/**
 * @typedef {{
 *  session?: Session
 *  publisher?: Publisher
 *  subscribers: Subscriber[]
 *  isConnected: boolean
 *  isLoading: boolean
 *  error?: any
 *  isMobile: boolean
 *  mediaSource: string
 *  joinSession: (sessionId: string) => void
 *  leaveSession: () => void
 *  toggleAudio: () => void
 *  toggleVideo: () => void
 *  switchMediaSource: (newMediaSource: any) => void
 *  clearError: () => void
 * }} OpenViduValue
 * 
 */
/** @type {React.Context<OpenViduValue>} */
const OpenViduContext = createContext();
/** 
 * @template T
 * @typedef {ReturnType<typeof useState<T>>} UseState
 */

// -eslint-disable-next-line react-refresh/only-export-components
export const useOpenVidu = () => {
  const context = useContext(OpenViduContext);
  if (!context) {
    throw new Error('useOpenVidu must be used within OpenViduProvider');
  }
  return context;
};

export const OpenViduProvider = ({ children }) => {
  /** @type {UseState<Session>} */
  const [session, setSession] = useState(null);
  /** @type {UseState<Publisher>} */
  const [publisher, setPublisher] = useState(null);
  /** @type {UseState<Subscriber[]>} */
  const [subscribers, setSubscribers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  /** @type {UseState<'camera' | 'screen'>} */
  const [mediaSource, setMediaSource] = useState('camera'); // 'camera' or 'screen'
  
  const OV = useRef(new OpenVidu());

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
      // Set default media source based on device
      setMediaSource(mobile ? 'camera' : 'screen');
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getToken = async (sessionId) => {
    try {
      console.log('🔑 Requesting token for session:', sessionId);
      const urlParams = new URLSearchParams(window.location.search)
      const token = urlParams.get('token')
      if (token) {
        return token;
      }
      
      const response = await axios.post(
        `/api/v2/sessions/${sessionId}/connections`,
        {
          clientData: 'React Client',
          platform: isMobile ? 'mobile' : 'desktop',
          mediaSource: mediaSource
        },
        {
          timeout: 10000,
          baseURL: SIGNALING_CONFIG.serverUrl,
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      
      console.log('✅ Token received');
      return response.data.token;
    } catch (error) {
      console.error('❌ Token request failed:', error);
      
      let errorMessage = 'Failed to get token from server';
      
      if (error.code === 'ECONNREFUSED') {
        errorMessage = 'Cannot connect to server. Make sure the backend is running.';
      } else if (error.response?.data?.error) {
        errorMessage = `${error.response.data.error}: ${error.response.data.details || ''}`;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  };

  const initPublisher = async () => {
    const publisherOptions = {
      audioSource: undefined, // Default microphone
      publishAudio: true,
      resolution: '640x480',
      frameRate: 30,
      insertMode: 'APPEND',
    };

    if (mediaSource === 'screen' && !isMobile) {
      // Screen sharing for desktop
      publisherOptions.videoSource = 'screen';
      publisherOptions.publishVideo = true;
      publisherOptions.mirror = false;
      
      try {
        const screenPublisher = OV.current.initPublisher(undefined, publisherOptions);
        
        screenPublisher.on('streamCreated', () => {
          console.log('🖥️ Screen share stream created');
        });
        
        screenPublisher.on('streamDestroyed', () => {
          console.log('🖥️ Screen share stream destroyed');
        });
        
        return screenPublisher;
      } catch (screenError) {
        console.warn('Screen sharing failed, falling back to camera:', screenError);
        // Fallback to camera
        setMediaSource('camera');
      }
    }

    // Camera for mobile or fallback
    publisherOptions.videoSource = undefined; // Default camera
    publisherOptions.publishVideo = true;
    publisherOptions.mirror = !isMobile; // Mirror for front camera on mobile
    
    const cameraPublisher = OV.current.initPublisher(undefined, publisherOptions);
    
    cameraPublisher.on('accessAllowed', () => {
      console.log('✅ Camera and microphone access allowed');
    });

    cameraPublisher.on('accessDenied', () => {
      console.error('❌ Camera and microphone access denied');
      setError('Camera and microphone access is required for video calls');
    });

    return cameraPublisher;
  };

  const joinSession = async (sessionId) => {
    if (isConnected) {
      console.log('⚠️ Already connected to session');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('🚀 Joining session:', sessionId);
      
      const token = await getToken(sessionId);
      
      // Initialize OpenVidu Session
      const newSession = OV.current.initSession();
      setSession(newSession);

      // Set up event handlers
      newSession.on('streamCreated', (event) => {
        console.log('📹 Stream created:', event.stream.streamId);
        const subscriber = newSession.subscribe(event.stream, undefined);
        setSubscribers(prev => [...prev, subscriber]);
      });

      newSession.on('streamDestroyed', (event) => {
        console.log('📹 Stream destroyed:', event.stream.streamId);
        setSubscribers(prev => prev.filter(sub => sub.stream.streamId !== event.stream.streamId));
      });

      // eslint-disable-next-line no-unused-vars
      newSession.on('sessionDisconnected', (event) => {
        console.log('🔌 Session disconnected');
        setIsConnected(false);
        setSession(null);
        setPublisher(null);
        setSubscribers([]);
      });

      newSession.on('exception', (exception) => {
        console.warn('⚠️ OpenVidu exception:', exception);
      });

      // Connect to the session
      console.log('🔗 Connecting to session with token...');
      await newSession.connect(token, { 
        clientData: JSON.stringify({
          name: isMobile ? 'Mobile User' : 'Desktop User',
          type: isMobile ? 'mobile' : 'desktop',
          mediaSource: mediaSource,
          joinTime: new Date().toISOString()
        })
      });

      console.log('✅ Successfully connected to session');

      // Initialize and publish stream
      // const newPublisher = await initPublisher();
      // await newSession.publish(newPublisher);
      // setPublisher(newPublisher);
      setIsConnected(true);
      
      console.log('🎉 Successfully joined session and published stream');

    } catch (error) {
      console.error('❌ Failed to join session:', error);
      setError(error.message);
      
      // Clean up on error
      if (session) {
        session.disconnect();
      }
      setSession(null);
      setPublisher(null);
      setSubscribers([]);
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  const switchMediaSource = async (newSource) => {
    if (!isConnected || !session || !publisher) return;
    
    if (newSource === mediaSource) return;
    
    try {
      console.log(`🔄 Switching media source from ${mediaSource} to ${newSource}`);
      
      // Unpublish current stream
      await session.unpublish(publisher);
      
      // Set new media source
      setMediaSource(newSource);
      
      // Initialize new publisher
      const newPublisher = await initPublisher();
      
      // Publish new stream
      await session.publish(newPublisher);
      setPublisher(newPublisher);
      
      console.log(`✅ Successfully switched to ${newSource}`);
    } catch (error) {
      console.error('❌ Failed to switch media source:', error);
      setError('Failed to switch media source');
    }
  };

  const leaveSession = () => {
    console.log('👋 Leaving session');
    
    if (session) {
      session.disconnect();
    }
    
    setSession(null);
    setPublisher(null);
    setSubscribers([]);
    setIsConnected(false);
    setError(null);
  };

  const toggleAudio = () => {
    if (publisher) {
      const newState = !publisher.stream.audioActive;
      publisher.publishAudio(newState);
      console.log('🎤 Audio', newState ? 'enabled' : 'disabled');
    }
  };

  const toggleVideo = () => {
    if (publisher) {
      const newState = !publisher.stream.videoActive;
      publisher.publishVideo(newState);
      console.log('📹 Video', newState ? 'enabled' : 'disabled');
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    session,
    publisher,
    subscribers,
    isConnected,
    isLoading,
    error,
    isMobile,
    mediaSource,
    joinSession,
    leaveSession,
    toggleAudio,
    toggleVideo,
    switchMediaSource,
    clearError,
  };

  return (
    <OpenViduContext.Provider value={value}>
      {children}
    </OpenViduContext.Provider>
  );
};